import { Controller } from "@hotwired/stimulus"

// Localization wizard Step 4 modal editor.
// Each feature has a single cover (one MediaItem). User sets the cover
// from Upload (file or Vimeo link) or Library (search/paginate/pick).
// Any upload/pick replaces any existing media for the feature — the
// server handles delete+create atomically via replace_existing=true.
export default class extends Controller {
  static targets = [
    "modal", "modalTitle", "modalSlug",
    "modalName", "modalDesc",
    "coverPreview", "coverHint",
    "tabUpload", "tabLibrary",
    "uploadPanel", "uploadInput", "uploadButton",
    "vimeoInput", "vimeoButton", "uploadError",
    "translateButton", "translateError",
    "translateAllButton", "translateAllStatus", "translateAllErrors",
    "libraryPanel", "librarySearch", "libraryGrid",
    "libraryPrev", "libraryNext", "libraryCounter",
    "card", "cardName", "cardMedia",
    "nameField", "descField"
  ]

  static values = {
    targetLocale: { type: String, default: "" },
    translateEndpoint: { type: String, default: "/admin/vehicle_localizations/translate_feature" },
    translateConfirmText: { type: String, default: "O conteúdo atual será substituído. Continuar?" },
    translateEmptyText: { type: String, default: "No source content was found to translate." },
    translateAllConfirmText: { type: String, default: "Translate every feature and replace its current content?" },
    translateAllProgressText: { type: String, default: "%{done} / %{total}" },
    translateAllDoneText: { type: String, default: "%{done} translated." },
    translateAllFailedText: { type: String, default: "%{failed} failed:" }
  }

  connect() {
    this._currentFeatureId = null
    this._libraryPage = 1
    this._libraryPages = 1
    this._libraryQuery = ""
    this._librarySearchTimer = null
  }

  // ─── Modal lifecycle ──────────────────────────────────────────

  open(event) {
    const featureId = event.currentTarget.dataset.featureId
    if (!featureId) return
    this._currentFeatureId = featureId

    const card = this._cardFor(featureId)
    if (!card) return

    const nameInput = this._nameFieldFor(featureId)
    const descInput = this._descFieldFor(featureId)

    this.modalTitleTarget.textContent = nameInput?.value || card.querySelector("[data-feature-editor-modal-target='cardName']")?.textContent || ""
    this.modalSlugTarget.textContent  = card.dataset.featureSlug || ""
    this.modalNameTarget.value        = nameInput?.value || ""

    // Lexical: push the description HTML through the rich-editor
    // Stimulus controller so the contenteditable surface re-renders.
    // Falls back to writing the hidden input directly when the editor
    // hasn't connected yet (rare — modal opens before frame stabilizes).
    this.modalDescTarget.value = descInput?.value || ""
    this._loadLexicalDesc(descInput?.value || "")

    // Default to upload tab
    this.showUpload()
    this._clearUploadError()
    this.uploadInputTarget.value = ""
    this.vimeoInputTarget.value = ""

    // Load current cover
    this._renderCoverLoading()
    this._fetchCurrentCover(featureId)
    this._clearTranslateError()
    if (this.hasTranslateButtonTarget) {
      this.translateButtonTarget.disabled = !card.dataset.sourceFeatureId
    }

    if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    } else {
      this.modalTarget.setAttribute("open", "")
    }
  }

  save() {
    if (!this._currentFeatureId) return
    const nameInput = this._nameFieldFor(this._currentFeatureId)
    const descInput = this._descFieldFor(this._currentFeatureId)
    if (nameInput) nameInput.value = this.modalNameTarget.value

    // The Lexical editor mirrors its content to the hidden input
    // (modalDescTarget) on every update via its registerUpdateListener
    // — so reading the input is enough; we don't need to query the
    // editor for the latest HTML.
    const descHtml = this.modalDescTarget.value
    if (descInput) descInput.value = descHtml || ""

    const card = this._cardFor(this._currentFeatureId)
    const preview = card?.querySelector("[data-feature-editor-modal-target='cardName']")
    if (preview) preview.textContent = this.modalNameTarget.value

    this._close()
  }

  async translateCurrent(event) {
    event?.preventDefault?.()
    if (!this._currentFeatureId || this._translationBusy) return

    const card = this._cardFor(this._currentFeatureId)
    const sourceFeatureId = card?.dataset.sourceFeatureId
    if (!sourceFeatureId) {
      this._showTranslateError(this.translateEmptyTextValue)
      return
    }

    const targetHasContent =
      this.modalNameTarget.value.trim().length > 0 ||
      this._stripHtml(this.modalDescTarget.value).trim().length > 0
    if (targetHasContent && !confirm(this.translateConfirmTextValue)) return

    this._translationBusy = true
    this._setTranslateBusy(true)
    this._clearTranslateError()

    const fd = this._wizardStateFormData()
    fd.append("source_feature_id", sourceFeatureId)
    fd.append("target_feature_id", this._currentFeatureId)
    fd.append("target_locale", this.targetLocaleValue)

    try {
      const response = await fetch(this.translateEndpointValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: fd,
        credentials: "same-origin"
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this._showTranslateError(data.error || `${response.status}`)
        return
      }

      this.modalNameTarget.value = data.name || ""
      this.modalDescTarget.value = data.description || ""
      this._loadLexicalDesc(data.description || "")
    } catch (_) {
      this._showTranslateError("Falha ao traduzir.")
    } finally {
      this._setTranslateBusy(false)
      this._translationBusy = false
    }
  }

  // Translates every card in turn through the same endpoint as the modal's
  // button, writing into each card's hidden fields. Nothing is persisted
  // until the user submits the step, so the results can still be reviewed.
  async translateAll(event) {
    event?.preventDefault?.()
    if (this._translationBusy) return
    const cards = this.cardTargets.filter(card => card.dataset.sourceFeatureId)
    if (cards.length === 0) return
    if (!confirm(this.translateAllConfirmTextValue)) return

    this._translationBusy = true
    this.translateAllButtonTarget.disabled = true
    this.translateAllButtonTarget.classList.add("loading")
    this.translateAllErrorsTarget.replaceChildren()
    this.translateAllErrorsTarget.classList.add("hidden")
    this.translateAllStatusTarget.classList.remove("hidden")

    const failures = []
    let done = 0
    for (const card of cards) {
      this.translateAllStatusTarget.textContent = this._format(this.translateAllProgressTextValue, { done: done + failures.length + 1, total: cards.length })
      const featureId = card.dataset.featureId
      const fd = this._wizardStateFormData()
      fd.append("source_feature_id", card.dataset.sourceFeatureId)
      fd.append("target_feature_id", featureId)
      fd.append("target_locale", this.targetLocaleValue)
      try {
        const response = await fetch(this.translateEndpointValue, {
          method: "POST", headers: this._jsonHeaders(), body: fd, credentials: "same-origin"
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok || !data.name) throw new Error(data.error || this.translateEmptyTextValue)

        const nameInput = this._nameFieldFor(featureId)
        const descInput = this._descFieldFor(featureId)
        if (nameInput) nameInput.value = data.name
        if (descInput) descInput.value = data.description || ""
        const preview = card.querySelector("[data-feature-editor-modal-target='cardName']")
        if (preview) preview.textContent = data.name
        done += 1
      } catch (error) {
        const name = card.querySelector("[data-feature-editor-modal-target='cardName']")?.textContent || card.dataset.featureSlug
        failures.push(`${name}: ${error.message || "erro"}`)
      }
    }

    this.translateAllStatusTarget.textContent = this._format(this.translateAllDoneTextValue, { done })
    if (failures.length > 0) {
      const heading = document.createElement("li")
      heading.className = "list-none -ml-5 font-semibold"
      heading.textContent = this._format(this.translateAllFailedTextValue, { failed: failures.length })
      this.translateAllErrorsTarget.append(heading)
      for (const line of failures) {
        const li = document.createElement("li")
        li.textContent = line
        this.translateAllErrorsTarget.append(li)
      }
      this.translateAllErrorsTarget.classList.remove("hidden")
    }
    this.translateAllButtonTarget.disabled = false
    this.translateAllButtonTarget.classList.remove("loading")
    this._translationBusy = false
  }

  _format(template, vars) {
    return Object.entries(vars).reduce((text, [key, value]) => text.replaceAll(`%{${key}}`, value), template)
  }

  cancel(event) {
    if (event) event.preventDefault()
    this._close()
  }

  _close() {
    if (typeof this.modalTarget.close === "function") {
      this.modalTarget.close()
    } else {
      this.modalTarget.removeAttribute("open")
    }
    this._currentFeatureId = null
  }

  // ─── Tab switching ────────────────────────────────────────────

  showUpload() {
    this.tabUploadTarget.classList.add("tab-active")
    this.tabLibraryTarget.classList.remove("tab-active")
    this.uploadPanelTarget.classList.remove("hidden")
    this.libraryPanelTarget.classList.add("hidden")
  }

  showLibrary() {
    this.tabUploadTarget.classList.remove("tab-active")
    this.tabLibraryTarget.classList.add("tab-active")
    this.uploadPanelTarget.classList.add("hidden")
    this.libraryPanelTarget.classList.remove("hidden")
    if (this.libraryGridTarget.children.length === 0) {
      this._libraryPage = 1
      this._fetchLibrary()
    }
  }

  // ─── Upload: file ─────────────────────────────────────────────

  uploadFile() {
    if (!this._currentFeatureId) return
    const file = this.uploadInputTarget.files[0]
    if (!file) {
      this._showUploadError("Selecione um arquivo primeiro.")
      return
    }

    this._clearUploadError()
    this.uploadButtonTarget.disabled = true

    const fd = new FormData()
    fd.append("media_item[file]", file)
    fd.append("media_item[media_type]", file.type.startsWith("video") ? "video" : "image")
    fd.append("media_item[mediable_type]", "Feature")
    fd.append("media_item[mediable_id]", this._currentFeatureId)
    fd.append("replace_existing", "true")

    this._postMediaItem(fd)
      .finally(() => { this.uploadButtonTarget.disabled = false })
  }

  // ─── Upload: Vimeo URL ────────────────────────────────────────

  uploadVimeo() {
    if (!this._currentFeatureId) return
    const { url, error } = this._normalizeVimeoUrl(this.vimeoInputTarget.value)
    if (error === "empty") {
      this._showUploadError("Cole o link do Vimeo primeiro.")
      return
    }
    if (error) {
      this._showUploadError("Link inválido — esperado um URL do Vimeo.")
      return
    }

    this._clearUploadError()
    this.vimeoButtonTarget.disabled = true

    const fd = new FormData()
    fd.append("media_item[media_type]", "video")
    fd.append("media_item[url]", url)
    fd.append("media_item[mediable_type]", "Feature")
    fd.append("media_item[mediable_id]", this._currentFeatureId)
    fd.append("replace_existing", "true")

    this._postMediaItem(fd)
      .then(() => { this.vimeoInputTarget.value = "" })
      .finally(() => { this.vimeoButtonTarget.disabled = false })
  }

  _normalizeVimeoUrl(raw) {
    const value = (raw || "").trim()
    if (!value) return { error: "empty" }

    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`

    try {
      const url = new URL(candidate)
      if (url.protocol === "http:") url.protocol = "https:"
      const host = url.hostname.toLowerCase()
      if (url.protocol !== "https:" || (host !== "vimeo.com" && !host.endsWith(".vimeo.com"))) {
        return { error: "invalid" }
      }
      return { url: url.toString() }
    } catch (_) {
      return { error: "invalid" }
    }
  }

  _postMediaItem(fd) {
    return fetch("/admin/media_items.json", {
      method: "POST",
      headers: this._jsonHeaders(),
      body: fd
    })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          this._showUploadError((data.errors || ["Falha."]).join(", "))
          return
        }
        this._renderCover(data)
        this._refreshCardPreview(data)
        // Replaced cover → any library tile for the previous cover is
        // now stale. Drop the cache so the next library open re-fetches.
        if (this.libraryGridTarget) this.libraryGridTarget.innerHTML = ""
      })
      .catch(() => this._showUploadError("Falha na operação."))
  }

  // ─── Library picker ───────────────────────────────────────────

  librarySearchChanged() {
    clearTimeout(this._librarySearchTimer)
    this._librarySearchTimer = setTimeout(() => {
      this._libraryQuery = this.librarySearchTarget.value.trim()
      this._libraryPage = 1
      this._fetchLibrary()
    }, 250)
  }

  libraryPrev() {
    if (this._libraryPage <= 1) return
    this._libraryPage -= 1
    this._fetchLibrary()
  }

  libraryNext() {
    if (this._libraryPage >= this._libraryPages) return
    this._libraryPage += 1
    this._fetchLibrary()
  }

  _fetchLibrary() {
    this.libraryGridTarget.innerHTML =
      "<div class='col-span-4 text-xs text-base-content/40'>Carregando…</div>"
    const params = new URLSearchParams()
    params.set("page", this._libraryPage)
    params.set("per_page", 24)
    if (this._libraryQuery) params.set("q", this._libraryQuery)

    fetch(`/admin/media_items.json?${params}`, {
      headers: this._jsonHeaders()
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(payload => {
        const items = payload.items || []
        const pagy  = payload.pagy  || { page: 1, pages: 1, count: items.length }
        this._libraryPages = pagy.pages || 1
        this._renderLibrary(items)
        this._updateLibraryPagination(pagy)
      })
      .catch(() => {
        this.libraryGridTarget.innerHTML =
          "<div class='col-span-4 text-xs text-error'>Falha ao carregar biblioteca.</div>"
      })
  }

  _renderLibrary(items) {
    if (!items.length) {
      this.libraryGridTarget.innerHTML =
        "<div class='col-span-4 text-xs text-base-content/40 py-6 text-center'>Nada encontrado.</div>"
      return
    }
    this.libraryGridTarget.innerHTML = ""
    items.forEach(mi => {
      const tile = document.createElement("button")
      tile.type = "button"
      tile.className = "relative aspect-video rounded overflow-hidden bg-base-200 group cursor-pointer ring-0 hover:ring-2 hover:ring-primary transition"
      tile.setAttribute("data-action", "click->feature-editor-modal#libraryPick")
      tile.setAttribute("data-media-id", mi.id)
      tile.setAttribute("data-media-type", mi.media_type || "")
      tile.innerHTML = this._mediaTileInner(mi) +
        `<span class="absolute top-1 right-1 bg-primary text-primary-content text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition">Escolher</span>`
      this.libraryGridTarget.appendChild(tile)
    })
  }

  _updateLibraryPagination(pagy) {
    this.libraryCounterTarget.textContent = `Página ${pagy.page || 1} de ${pagy.pages || 1} · ${pagy.count || 0}`
    this.libraryPrevTarget.disabled = (pagy.page || 1) <= 1
    this.libraryNextTarget.disabled = (pagy.page || 1) >= (pagy.pages || 1)
  }

  libraryPick(event) {
    if (!this._currentFeatureId) return
    const button = event.currentTarget
    const sourceId = button.dataset.mediaId
    if (!sourceId) return

    button.disabled = true

    const fd = new FormData()
    fd.append("duplicate_from_id", sourceId)
    fd.append("mediable_type", "Feature")
    fd.append("mediable_id", this._currentFeatureId)
    fd.append("replace_existing", "true")

    fetch("/admin/media_items.json", {
      method: "POST",
      headers: this._jsonHeaders(),
      body: fd
    })
      .then(r => {
        // 404 means the library tile pointed at a MediaItem that was
        // destroyed by a previous replace_existing pick. Surface it and
        // refresh the library so stale tiles disappear.
        if (r.status === 404) {
          this._showUploadError("Essa mídia não está mais disponível. Lista atualizada.")
          this._fetchLibrary()
          throw new Error("stale")
        }
        return r.json().then(data => ({ ok: r.ok, data }))
      })
      .then(({ ok, data }) => {
        if (!ok) throw new Error("pick failed")
        this._renderCover(data)
        this._refreshCardPreview(data)
        // Refresh the library so the tile the user just "consumed"
        // (and any items that were destroyed via replace_existing) no
        // longer appear, and new ones show.
        this._fetchLibrary()
      })
      .catch(err => {
        if (err && err.message === "stale") return
        this._showUploadError("Falha ao escolher.")
      })
      .finally(() => { button.disabled = false })
  }

  // ─── Cover preview + card strip ───────────────────────────────

  _fetchCurrentCover(featureId) {
    const params = new URLSearchParams({
      mediable_type: "Feature",
      mediable_id:   featureId,
      per_page:      1
    })
    fetch(`/admin/media_items.json?${params}`, {
      headers: this._jsonHeaders()
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(payload => {
        const cover = (payload.items || [])[0]
        if (cover) {
          this._renderCover(cover)
        } else {
          this._renderNoCover()
        }
      })
      .catch(() => this._renderNoCover())
  }

  _renderCoverLoading() {
    this.coverPreviewTarget.innerHTML =
      `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/40">…</div>`
  }

  _renderNoCover() {
    this.coverPreviewTarget.innerHTML =
      `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/40 text-center px-2">Sem capa</div>`
  }

  _renderCover(mi) {
    this.coverPreviewTarget.innerHTML = this._mediaTileInner(mi)
  }

  _mediaTileInner(mi) {
    const src = mi.variant_url || mi.thumbnail_url || mi.url || ""
    if (mi.media_type === "video") {
      if (src) {
        return `
          <img src="${this._escape(src)}" class="w-full h-full object-cover" loading="lazy" alt="" />
          <div class="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white/90" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
            </svg>
          </div>`
      }
      return `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/50">video</div>`
    }
    if (src) {
      return `<img src="${this._escape(src)}" class="w-full h-full object-cover" loading="lazy" alt="" />`
    }
    return `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/50">${this._escape(mi.media_type || "")}</div>`
  }

  _refreshCardPreview(mi) {
    if (!this._currentFeatureId) return
    const card = this._cardFor(this._currentFeatureId)
    if (!card) return
    const strip = card.querySelector("[data-feature-editor-modal-target='cardMedia']")
    if (!strip) return

    const wrap = document.createElement("div")
    wrap.className = "w-12 h-12 rounded bg-base-200 overflow-hidden border border-base-100 ring-1 ring-base-200 relative"
    wrap.innerHTML = this._mediaTileInner(mi)

    strip.innerHTML = ""
    strip.appendChild(wrap)
  }

  // ─── Helpers ──────────────────────────────────────────────────

  _cardFor(featureId) {
    return this.cardTargets.find(el => el.dataset.featureId === featureId)
  }

  _nameFieldFor(featureId) {
    return this.nameFieldTargets.find(el => el.dataset.featureId === featureId)
  }

  _descFieldFor(featureId) {
    return this.descFieldTargets.find(el => el.dataset.featureId === featureId)
  }

  _wizardStateFormData() {
    const fd = new FormData()
    const current = new FormData(this.element)
    for (const [key, value] of current.entries()) {
      if (key.startsWith("feature_edits")) continue
      fd.append(key, value)
    }
    return fd
  }

  _loadLexicalDesc(html) {
    // The rich_editor partial tags its wrapper with
    // [data-rich-editor-for="<input_id>"]. The vehicle-localizations
    // feature editor uses a fixed input id, so the lookup is direct.
    const editorEl = this.modalTarget.querySelector('[data-rich-editor-for="feature_editor_modal_desc"]')
    if (!editorEl) return
    const ctrl = this.application.getControllerForElementAndIdentifier(editorEl, "lexical-editor")
    if (ctrl && typeof ctrl.load === "function") ctrl.load(html || "")
  }

  _jsonHeaders() {
    return {
      "Accept": "application/json",
      "X-CSRF-Token": this._csrfToken(),
      "X-Requested-With": "XMLHttpRequest"
    }
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _showUploadError(msg) {
    this.uploadErrorTarget.textContent = msg
    this.uploadErrorTarget.classList.remove("hidden")
  }

  _clearUploadError() {
    this.uploadErrorTarget.textContent = ""
    this.uploadErrorTarget.classList.add("hidden")
  }

  _setTranslateBusy(busy) {
    if (!this.hasTranslateButtonTarget) return
    this.translateButtonTarget.disabled = busy
    this.translateButtonTarget.classList.toggle("loading", busy)
  }

  _showTranslateError(msg) {
    if (!this.hasTranslateErrorTarget) return
    this.translateErrorTarget.textContent = msg
    this.translateErrorTarget.classList.remove("hidden")
  }

  _clearTranslateError() {
    if (!this.hasTranslateErrorTarget) return
    this.translateErrorTarget.textContent = ""
    this.translateErrorTarget.classList.add("hidden")
  }

  _stripHtml(html) {
    if (!html) return ""
    const tmp = document.createElement("div")
    tmp.innerHTML = html
    return tmp.textContent || ""
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
